import { getRequest, postHtmlRequest, postRequest } from "../utils/fetch";
import { GridMode } from "../components/technical/grid";
import { StoreType } from "./storageService";

const basePath = "/ApiCore/MyPhotosApi";

interface ICreateFolderRequest {
	readonly name: string;
}

interface IRenameFolderRequest {
	readonly folderId: string;
	readonly newName: string;
}

interface IPhotoDetailsRequest {
	readonly photoId: string;
}

interface IDeletePhotosRequest {
	readonly folderId: string;
	readonly pointOfSaleId: string;
	readonly photoIds: string[];
	readonly useDeselected: boolean;
}

interface IDeletePhotoRequest {
	readonly photoId: string;
	readonly pointOfSaleId: string;
}

export interface IPhotoDetails {
	readonly modified: string;
	readonly dateTaken: string;
	readonly dateAdded: string;
	readonly fileSize: number;
	readonly album: string;
	readonly fileName: string;
	readonly resolution: string;
}

interface IMovePhotoRequest {
	readonly newAlbum: boolean;
	readonly photoIds: string[];
	readonly fromAlbumId?: string;
	readonly toAlbumId: string;
}

export interface IRotatePhoto {
	photoId: string;
	rotation: number;
}

export interface IRedirectUrlResponse {
	readonly redirectUrl: string;
}

interface IRotatePhotoRequest {
	rotationRequests: IRotatePhoto[];
}

interface IDeleteAlbumRequest {
	readonly folderIds: string[];
	readonly useDeselected: boolean;
}

interface GridItemAmountResponse {
	readonly amount: number;
}

export function createalbum(name: string): Promise<IRedirectUrlResponse> {
	return postRequest<ICreateFolderRequest, IRedirectUrlResponse>(
		basePath + "/createalbum",
		{ name: name } as ICreateFolderRequest,
		{ headers: { "Content-type": "application/json; charset=utf-8" } },
	);
}

export function renameAlbum(folderId: string, name: string): Promise<void> {
	return postRequest<IRenameFolderRequest, void>(
		basePath + "/renameAlbum",
		{ folderId: folderId, newName: name } as IRenameFolderRequest,
		{ headers: { "Content-type": "application/json; charset=utf-8" } },
	);
}

export function deleteAlbums(folderIds: string[], useDeselected: boolean): Promise<void> {
	return postRequest<IDeleteAlbumRequest, void>(
		"/apicore/myphotosapi/deletealbums",
		{ folderIds: folderIds, useDeselected },
		{ headers: { "Content-type": "application/json; charset=utf-8" } },
	);
}

export function deletePhotos(
	photoIds: string[],
	folderId: string,
	useDeselected: boolean,
	page?: number,
): Promise<void> {
	return postRequest<IDeletePhotosRequest, void>(
		basePath + "/deletePhotos",
		{ folderId, photoIds, pointOfSaleId: window.context.pointOfSaleId, useDeselected, page } as IDeletePhotosRequest,
		{ headers: { "Content-type": "application/json; charset=utf-8" } },
	);
}

export function deletePhoto(photoId: string): Promise<void> {
	return postRequest<IDeletePhotoRequest, void>(
		basePath + "/deletePhoto",
		{ photoId, pointOfSaleId: window.context.pointOfSaleId } as IDeletePhotoRequest,
		{ headers: { "Content-type": "application/json; charset=utf-8" } },
	);
}

export function getPhotoDetails(photoId: string): Promise<IPhotoDetails> {
	return postRequest<IPhotoDetailsRequest, IPhotoDetails>(
		basePath + "/photoDetails",
		{ photoId } as IPhotoDetailsRequest,
		{ headers: { "Content-type": "application/json; charset=utf-8" } },
	);
}

export function movePhotos(
	newAlbum: boolean,
	useDeselected: boolean,
	photoIds: string[],
	fromAlbumId: string,
	toAlbumId?: string,
) {
	return postRequest<IMovePhotoRequest, IRedirectUrlResponse>(
		basePath + "/movePhotos",
		{ newAlbum, useDeselected, photoIds, fromAlbumId, toAlbumId } as IMovePhotoRequest,
		{ headers: { "Content-type": "application/json; charset=utf-8" } },
	);
}

export function rotatePhotos(rotationRequests: IRotatePhoto[]) {
	return postRequest<IRotatePhotoRequest, void>(
		basePath + "/rotatePhotos",
		{ rotationRequests } as IRotatePhotoRequest,
		{ headers: { "Content-type": "application/json; charset=utf-8" } },
	);
}

export function getGridBody(
	gridMode: GridMode,
	store: StoreType,
	page: number,
	sortDirection: number,
	folderColumns: number,
	folderId?: string,
	isImportPhotos?: boolean,
	maxSelectableItems = 0,
) {
	return postHtmlRequest(
		basePath + "/getGridBody",
		{
			gridMode,
			folderId: folderId || "",
			page,
			sortDirection,
			folderColumns,
			store,
			isImportPhotos,
			maxSelectableItems,
		},
		{ headers: { "Content-type": "application/json; charset=utf-8" } },
	);
}

export function getGridItems(
	gridMode: GridMode,
	store: StoreType,
	page: number,
	pageToken: string,
	sortDirection: number,
	folderColumns: number,
	folderId?: string,
	isImportPhotos?: boolean,
	excludeFolderId?: string,
	itemsSelectable = true,
) {
	return postHtmlRequest(
		basePath + "/GetGridItems",
		{
			gridMode,
			folderId: folderId || "",
			page,
			sortDirection,
			folderColumns,
			isImportPhotos,
			excludeFolderId,
			store,
			pageToken,
			itemsSelectable,
		},
		{ headers: { "Content-type": "application/json; charset=utf-8" } },
	);
}

export function getGridItemAmount(folderId: string): Promise<number> {
	return getRequest<GridItemAmountResponse>(basePath + "/GetGridItemAmount?folderid=" + folderId, {
		headers: { "Content-type": "application/json; charset=utf-8" },
	}).then((res) => res.amount);
}

export function getAllAlbumIds(): Promise<{ albumids: string[]; totalAlbumCount: number; totalAmountOfItems: number }> {
	return getRequest<{ albumids: string[]; totalAlbumCount: number; totalAmountOfItems: number }>(
		"/apicore/myphotosapi/getallalbumids",
	);
}
